(() => {
  'use strict';
  if (window.AlbumTagsStudio) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = n => Math.max(0, Math.min(1, n));
  const ease = n => { n=clamp(n); return n*n*(3-2*n); };
  const objects = new Set(), androids = new Set(), visible = new WeakSet();
  let frame=0, androidFrame=0, fitFrame=0;
  const motion = () => !reduced.matches && document.body.dataset.motionEnabled !== 'false';
  const observer = new IntersectionObserver(entries => {
    entries.forEach(({target,isIntersecting}) => isIntersecting ? visible.add(target) : visible.delete(target));
    queue(); startAndroid();
  },{rootMargin:'100px'});
  function set(el,name,value){ el.style.setProperty(name,value); }
  // Reserve room for editable copy before sizing the complete product scene.
  // Width and height change together; the calibrated case/disc geometry stays intact.
  function fitObject(el) {
    const sticky=el.querySelector('.studio-object-sticky'), scene=el.querySelector('.studio-object-scene');
    if(!sticky||!scene)return;
    const viewport=document.documentElement.clientHeight;
    const header=document.querySelector('[data-studio-header]');
    const pinTop=Math.ceil((header?.getBoundingClientRect().bottom||0)+12);
    const available=viewport-pinTop-16;
    const style=getComputedStyle(sticky), sceneStyle=getComputedStyle(scene);
    const number=v=>parseFloat(v)||0;
    const outerHeight=node=>{
      if(!node)return 0;
      const css=getComputedStyle(node);
      return node.getBoundingClientRect().height+number(css.marginTop)+number(css.marginBottom);
    };
    const reserved=number(style.paddingTop)+number(style.paddingBottom)
      +outerHeight(el.querySelector('.studio-intro'))
      +outerHeight(el.querySelector('.studio-product-rail'))
      +outerHeight(el.querySelector('.studio-product-outro'))
      +number(sceneStyle.marginTop)+number(sceneStyle.marginBottom);
    const fullWidth=sticky.clientWidth-number(style.paddingLeft)-number(style.paddingRight);
    // On mobile the open case, disc and quarter stack vertically. Keep the
    // scene large; short screens use normal document flow instead of shrinking it.
    const small=innerWidth<750;
    const ratio=small?1/1.22:3.35;
    const naturalHeight=fullWidth/ratio;
    const sceneHeight=Math.min(naturalHeight,Math.max(0,available-reserved));
    const minimumHeight=small?naturalHeight*.86:Math.min(180,naturalHeight*.8);
    const pinned=motion()&&el.dataset.animationEnabled!=='false'&&viewport>=500&&sceneHeight>=minimumHeight;
    el.dataset.productLayout=pinned?'pinned':'flow';
    set(el,'--product-pin-top',pinTop+'px');
    set(el,'--product-scene-width',pinned?Math.floor(sceneHeight*ratio)+'px':'100%');
    set(el,'--product-frame-height',Math.ceil(sticky.getBoundingClientRect().height)+'px');
    set(el,'--product-scroll-distance',Math.round(Math.max(360,Math.min(800,viewport*.65)))+'px');
  }
  function fitObjects(){
    fitFrame=0;
    for(const el of objects)if(el.isConnected)fitObject(el);
    queue();
  }
  function queueFit(){if(!fitFrame)fitFrame=requestAnimationFrame(fitObjects);}
  const fitObserver=new ResizeObserver(queueFit);
  function paintObject(el) {
    const scene=el.querySelector('.studio-object-scene');
    if (!scene) return;
    const rect=el.getBoundingClientRect();
    const animated=motion() && el.dataset.animationEnabled!=='false';
    const sticky=el.querySelector('.studio-object-sticky');
    const stickyTop=parseFloat(getComputedStyle(sticky).top)||0;
    const small=innerWidth<750;
    let p;
    if(animated && el.dataset.productLayout==='pinned') {
      p=clamp((stickyTop-rect.top)/Math.max(1,el.offsetHeight-sticky.offsetHeight));
    } else if(animated && small && el.dataset.productLayout==='flow') {
      // Short phones keep their full-width scene and play the same sequence
      // as it enters the viewport, without trapping or delaying page scrolling.
      // A stage button holds its chosen view until the reader scrolls again.
      const manual=el.dataset.manualProgress;
      const holding=manual!==undefined && Math.abs(scrollY-Number(el.dataset.manualScrollY??scrollY))<=24;
      if(holding) {
        p=clamp(Number(manual));
      } else {
        delete el.dataset.manualProgress;
        delete el.dataset.manualScrollY;
        const viewport=document.documentElement.clientHeight;
        const travel=Math.max(220,Math.min(300,viewport*.52));
        const entering=Math.min(viewport*.82,viewport-72);
        p=clamp((entering-scene.getBoundingClientRect().top)/travel);
      }
    } else {
      p=clamp(Number(el.dataset.manualProgress??1));
    }
    const opening=ease((p-.08)/.28), extraction=ease((p-.42)/.28), coin=ease((p-.77)/.20);
    const w=scene.clientWidth, h=scene.clientHeight;
    set(el,'--closed-opacity',1-opening);
    set(el,'--closed-rotation',(-88*opening)+'deg');
    set(el,'--closed-x',(-w*.24*opening)+'px');
    set(el,'--open-opacity',opening);
    set(el,'--open-rotation',(40*(1-opening))+'deg');
    // The mobile tray hub sits at (75.594%, 26.059%) of scene width.
    // Roll the same-size disc down to the centre, below the opened case.
    set(el,'--disc-x',((small?-.255940594059:.29)*w*extraction)+'px');
    set(el,'--disc-y',(small?.439405940594*w*extraction:0)+'px');
    set(el,'--disc-turn',(300*extraction)+'deg');
    set(el,'--quarter-opacity',coin);
    set(el,'--quarter-x',(small?0:w*.4*(1-coin))+'px');
    set(el,'--quarter-y',(small?w*.34*(1-coin):0)+'px');
    set(el,'--quarter-turn',(-360*(1-coin))+'deg');
    set(el,'--object-progress',p);
    el.dataset.progress=p.toFixed(3);
    el.dataset.phase=p<.35?'closed':p<.7?'disc': 'scale';
    const active=p<.30?0:p<.69?1:2;
    el.querySelectorAll('[data-object-caption]').forEach((caption,i)=>{
      caption.classList.toggle('is-active',i===active);
      caption.setAttribute('aria-pressed',String(i===active));
    });
  }
  function update(){
    frame=0;
    const header=document.querySelector('[data-studio-header]');
    header?.classList.toggle('is-scrolled',scrollY>28);
    const hero=document.querySelector('[data-hero-art]');
    if(hero && motion() && hero.getBoundingClientRect().bottom>0){set(hero,'--hero-y',Math.min(45,scrollY*.08)+'px');set(hero,'--hero-turn',Math.min(9,scrollY*.014)+'deg');}
    for(const el of objects){if(!el.isConnected){objects.delete(el);observer.unobserve(el);continue;}if(visible.has(el)||!motion())paintObject(el);}
  }
  function queue(){if(!frame&&!document.hidden)frame=requestAnimationFrame(update);}
  function androidReady(el){return el.isConnected && el.closest('details')?.open && visible.has(el) && !document.hidden;}
  function paintAndroid(time){
    androidFrame=0;
    let again=false;
    for(const el of androids){
      if(!el.isConnected){androids.delete(el);observer.unobserve(el);continue;}
      const ready=androidReady(el); el.dataset.running=String(ready&&motion());
      if(!ready)continue;
      if(motion()){
        again=true; const p=((time-Number(el.dataset.started||time))%4200)/4200;
        const approach=ease(p/.3),pulse=Math.sin(clamp((p-.28)/.5)*Math.PI);
        set(el,'--android-y',(-45*(1-approach))+'px');set(el,'--android-turn',(-12*(1-approach))+'deg');set(el,'--android-pulse',pulse*.8);set(el,'--android-scale',.6+p*.6);
      }else{set(el,'--android-y','0px');set(el,'--android-turn','0deg');set(el,'--android-pulse',.6);set(el,'--android-scale',1);}
    }
    if(again)androidFrame=requestAnimationFrame(paintAndroid);
  }
  function startAndroid(){if(!androidFrame)androidFrame=requestAnimationFrame(paintAndroid);}
  function selectTab(tab,focus=false){
    const container=tab.closest('[data-tabs]'); if(!container)return;
    container.querySelectorAll('[role=tab]').forEach(item=>{const active=item===tab;item.setAttribute('aria-selected',String(active));item.tabIndex=active?0:-1;const panel=document.getElementById(item.getAttribute('aria-controls'));if(panel)panel.hidden=!active;});
    if(focus)tab.focus();
  }
  function closeMenu(focus=false){const toggle=document.querySelector('[data-menu-toggle]'), menu=document.getElementById('StudioMobileMenu');if(!toggle||!menu)return;menu.hidden=true;toggle.setAttribute('aria-expanded','false');document.body.classList.remove('menu-open');if(focus)toggle.focus();}
  document.addEventListener('click',event=>{
    const tab=event.target.closest('[role=tab]');if(tab)selectTab(tab);
    const seek=event.target.closest('[data-object-seek]');
    if(seek){
      const track=seek.closest('[data-object-scroll]'),sticky=track.querySelector('.studio-object-sticky'),p=[0,.47,.99][Number(seek.dataset.objectSeek)];
      if(motion()&&track.dataset.productLayout==='pinned'){
        scrollTo({top:scrollY+track.getBoundingClientRect().top-parseFloat(getComputedStyle(sticky).top)+p*Math.max(1,track.offsetHeight-sticky.offsetHeight),behavior:'smooth'});
      }else{track.dataset.manualProgress=p;track.dataset.manualScrollY=String(scrollY);paintObject(track);}
    }
    const toggle=event.target.closest('[data-menu-toggle]');
    if(toggle){const menu=document.getElementById(toggle.getAttribute('aria-controls'));const open=toggle.getAttribute('aria-expanded')!=='true';toggle.setAttribute('aria-expanded',String(open));menu.hidden=!open;document.body.classList.toggle('menu-open',open);}
    if(event.target.closest('.studio-mobile-menu a'))closeMenu();
  });
  document.addEventListener('keydown',event=>{
    if(event.key==='Escape')closeMenu(true);
    const tab=event.target.closest('[role=tab]');
    if(tab&&['ArrowLeft','ArrowRight','Home','End'].includes(event.key)){
      event.preventDefault();const tabs=[...tab.closest('[role=tablist]').querySelectorAll('[role=tab]')];const i=tabs.indexOf(tab);const next=event.key==='Home'?0:event.key==='End'?tabs.length-1:(i+(event.key==='ArrowRight'?1:-1)+tabs.length)%tabs.length;selectTab(tabs[next],true);
    }
    if(event.key==='Tab'&&document.body.classList.contains('menu-open')){
      const controls=[...document.querySelectorAll('.studio-header a,.studio-header button')].filter(el=>el.getClientRects().length);
      const first=controls[0],last=controls[controls.length-1];
      if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
    }
  });
  document.addEventListener('toggle',event=>{
    if(!event.target.matches('.studio-faq-item'))return;
    const el=event.target.querySelector('[data-android]');if(el){if(event.target.open)el.dataset.started=performance.now();else el.dataset.running='false';startAndroid();}
  },true);
  function initialize(scope=document){
    scope.querySelectorAll('[data-object-scroll]').forEach(el=>{
      if(!objects.has(el)){
        objects.add(el);observer.observe(el);fitObserver.observe(el);
        el.querySelectorAll('.studio-intro,.studio-product-rail,.studio-product-outro').forEach(node=>fitObserver.observe(node));
      }
      fitObject(el);paintObject(el);
    });
    scope.querySelectorAll('[data-android]').forEach(el=>{if(!androids.has(el)){androids.add(el);observer.observe(el);el.dataset.running='false';}});
    queue();
  }
  addEventListener('scroll',queue,{passive:true});
  addEventListener('resize',()=>{if(innerWidth>1000)closeMenu();queueFit();queue();});
  document.addEventListener('visibilitychange',()=>{if(document.hidden){cancelAnimationFrame(frame);frame=0;cancelAnimationFrame(androidFrame);androidFrame=0;for(const el of androids)el.dataset.running='false';}else{queue();startAndroid();}});
  reduced.addEventListener('change',()=>{queueFit();queue();startAndroid();});
  new MutationObserver(()=>{queueFit();queue();startAndroid();}).observe(document.body,{attributes:true,attributeFilter:['data-motion-enabled']});
  document.addEventListener('shopify:section:load',event=>initialize(event.target));
  document.addEventListener('shopify:section:unload',queue);
  document.addEventListener('shopify:block:select',event=>{const blockId=event.detail?.blockId;const tab=blockId&&document.querySelector('[role=tab][id$="-'+CSS.escape(blockId)+'"]');if(tab)selectTab(tab);const details=event.target.closest('details');if(details)details.open=true;});
  window.AlbumTagsStudio={initialize};
  const fitHeader=document.querySelector('[data-studio-header]');
  if(fitHeader)fitObserver.observe(fitHeader);
  document.fonts?.ready.then(queueFit);
  initialize();
})();
